import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton, GlassButtonRow } from "./GlassButtons";
import { useWindowContext, isWindowOpen } from "src/WindowManager/useWindowContext";
import { closeWindow, ManagedWindow, openWindow, WindowId } from "src/WindowManager/WindowState";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { GlassInputControl, GlassInputControlLabel, GlassInputGroup, GlassInputGroupTitle } from "./GlassInput";
import { SelectLayer } from "./CommonInputs";
import { useContext, useEffect, useMemo, useState, useRef } from "react";
import { MapitStateActionType, MapitStateContext } from "src/states/MapitState";
import { LayerInfo, LayerVariabilityType, LayerHierarchy, LayerHierarchyEndNode } from "src/common/managers/Types";
import { bbox, buffer } from "@turf/turf";
import type { FeatureCollection } from 'geojson';
type WFSFeatureCollection = FeatureCollection & { metadata?: { endpoint?: string; numberMatched?: number; numberReturned?: number } };
import { addFill, addOutline, addPoint, removeLayer, setGeom } from 'src/utils/mapLayerHelpers';
import { DataCollector } from "src/dataCollector/DataCollector";
import { Persistence } from "src/managers/Persistence";
import { OldHierarchy } from "src/managers/OldHierarchy";
import { ProtectedFeature } from "./ProtectedFeature";
import { ApplicationStateContext, Feature, FeatureType } from "src/states/ApplicationState";
import { Spinner } from "react-bootstrap";
import { ViewButtonText } from "src/propertyInfoTemplates/PropertyComponents";
import { wfsEndpoints } from "src/dataCollector/WFSCollectorEndpoints";
import { message } from "./Message";

export type PendingConflict = { result: WFSFeatureCollection | any; matchedCount: number; title?: string; source: string; groupName: string; layerName: string, err?: string };
export function ConflictSearchDialog({ window }: { window: ManagedWindow }) {
   const { state: windowState, dispatch: windowDispatch } = useWindowContext();
   const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);
   const [selectedLayer, setSelectedLayerId] = useState<number | undefined>(undefined);
   const [bufferDistance, setBufferDistance] = useState<number>(0);
   const [foundFeatures, setFoundFeatures] = useState<WFSFeatureCollection | null>(null);
   const [endpoint, setEndpoint] = useState<keyof typeof wfsEndpoints>("Matriklen2");
   const [isDownloading, setIsDownloading] = useState<boolean>(false);
   const [whoAmI, setWhoAmI] = useState<string | null>(null);
   const [error, setError] = useState<string | null>(null);
   const [resultMessage, setResultMessage] = useState<string | null>(null);
   const [pendingConflicts, setPendingConflicts] = useState<PendingConflict[]>([]);
   const [featureType, setFeatureType] = useState<keyof typeof wfsEndpoints[typeof endpoint]["featureTypes"] | undefined>();

   type WFSEndpointsType = { source: string, layer: string, title?: string };
   type Profile = { id: string; name: string; layers: WFSEndpointsType[]; feature: FeatureType };

   const profiles: Profile[] = [
      { id: "profile_test", name: "Test Profile", layers: [{ source: "Matriklen2", layer: "SamletFastEjendom_Gaeldende" }, { source: "Matriklen2", layer: "Matrikelskel_Gaeldende" }, { source: "Matriklen2", layer: "Skelpunkt_Gaeldende" }], feature: Feature.Debugging },
      { id: "profile_error", name: "Profile with error layer", layers: [{ source: "Matriklen2", layer: "XXSamletFastEjendom_Gaeldende" }, { source: "Matriklen2", layer: "Matrikelskel_Gaeldende" }, { source: "Matriklen2", layer: "XXSkelpunkt_Gaeldende" }], feature: Feature.Debugging },
      {
         id: "profile_CTO",
         name: "Bæredygtig Udvikling",
         feature: Feature.ConflictSearch,
         layers: [
            { source: "Vejdirektoratet (Statsvej)", layer: "Vejbyggelinje(Statsvej)", title: "Vejbyggelinje (Statsvej)" },
            { source: "Miljø Portal", layer: "2008 Kirkebyggelinjer", title: "Kirkebyggelinjer" },
            { source: "Miljø Portal", layer: "2005 Skovbyggelinjer", title: "Skovbyggelinjer" },
            { source: "Miljø Portal", layer: "2003 Søbeskyttelseslinjer", title: "Søbeskyttelseslinjer" },
            { source: "Miljø Portal", layer: "2004 Åbeskyttelseslinjer", title: "Åbeskyttelseslinjer" },
            { source: "Miljø Portal", layer: "2018 NATURA 2000 - Fuglebeskyttelse", title: "NATURA 2000 - Fuglebeskyttelse" },
            { source: "Miljø Portal", layer: "2019 NATURA 2000 - Habitatområder", title: "NATURA 2000 - Habitatområder" },
            { source: "DKJord", layer: "Jordforurening, V1 kortlagt", title: "Jordforurening, V1 kortlagt" },
            { source: "DKJord", layer: "Jordforurening, V2 kortlagt", title: "Jordforurening, V2 kortlagt" },
            { source: "Miljøgis", layer: "Fredskov", title: "Fredskov" },
            { source: "Matriklen2", layer: "KlitfredningFlade_Gaeldende", title: "KlitfredningFlade" },
            { source: "Matriklen2", layer: "StrandbeskyttelseFlade_Gaeldende", title: "Strandbeskyttelsesflade" },
            { source: "Miljø Portal", layer: "2013 Beskyttede naturtyper", title: "Beskyttede naturtyper" },
            { source: "Miljø Portal", layer: "2014 Beskyttede vandløb", title: "Beskyttede vandløb" },
            { source: "Miljø Portal", layer: "2002 Beskyttede sten- og jorddiger", title: "Beskyttede sten- og jorddiger" },
            { source: "Miljø Portal", layer: "2009 Fredede områder", title: "Fredede områder" },
            { source: "FVM (Føde, Land og Fisk)", layer: "GLM_Fortidsminder_2026", title: "Fredede fortidsminder" },
            { source: "Plandata.dk", layer: "theme-knz-kystnaerhedszone-polygon", title: "Kystnærhedszone" },
         ]
      },
      {
         id: "profile_Landbrugsmæglere",
         name: "Landbrugsmæglere",
         feature: Feature.ConflictSearch,
         layers: [
            { source: "Matriklen2", layer: "SamletFastEjendom_Gaeldende", title: "Samlet fast ejendom" },
            { source: "Matriklen2", layer: "Matrikelskel_Gaeldende", title: "Matrikelskel" },
            { source: "Matriklen2", layer: "Skelpunkt_Gaeldende", title: "Skelpunkt" },
            { source: "Miljøgis", layer: "drikkevandsinteresser", title: "Drikkevandsinteresser" },
            { source: "Miljøgis", layer: "vp3e2022_Nitratfølsomme indvindingsområder", title: "Nitratfølsomme indvindingsområder" },
            { source: "Miljø Portal", layer: "2142 Kommunernes status for boringsnære beskyttelsesområder (BNBO)", title: "Kommunernes status for boringsnære beskyttelsesområder (BNBO)" },
            { source: "Miljøgis", layer: "indsatsomraader", title: "Indsatsområder" },
            { source: "Miljøgis", layer: "indvindingsoplande_udenfor_osd", title: "Indvindingsoplande udenfor OSD" },
            { source: "Miljøgis", layer: "indvindingsoplande_alle", title: "Indvindingsoplande, Alle" },
            { source: "Miljøgis", layer: "foelsommeindvindingsomraader", title: "Følsomme indvindingsområder" },
            { source: "Miljø Portal", layer: "2014 Beskyttede vandløb", title: "Beskyttede vandløb" },
            { source: "Miljø Portal", layer: "2018 NATURA 2000 - Fuglebeskyttelse", title: "NATURA 2000 - Fuglebeskyttelse" },
            { source: "Miljø Portal", layer: "2019 NATURA 2000 - Habitatområder", title: "NATURA 2000 - Habitatområder" },
            { source: "Miljøgis", layer: "NP2-2016 Ramsarområder", title: "Ramsarområder NP2" },
            { source: "Miljø Portal", layer: "2020 Ramsar områder", title: "Ramsar områder" },
            { source: "Miljøgis", layer: "np11fredede_omraader_dai", title: "Fredede områder NP11" },
            { source: "Miljø Portal", layer: "2009 Fredede områder", title: "Fredede områder" },
            { source: "Miljø Portal", layer: "2140 Bekendtgørelsesfredninger", title: "Bekendtgørelsesfredninger" },
            { source: "FVM (Føde, Land og Fisk)", layer: "GLM_Fortidsminder_2026", title: "GLM Fortidsminder" },
            { source: "Miljø Portal", layer: "2003 Søbeskyttelseslinjer", title: "Søbeskyttelseslinjer" },
            { source: "Miljø Portal", layer: "2004 Åbeskyttelseslinjer", title: "Åbeskyttelseslinjer" },
            { source: "Miljø Portal", layer: "2008 Kirkebyggelinjer", title: "Kirkebyggelinjer" },
            { source: "Miljø Portal", layer: "2005 Skovbyggelinjer", title: "Skovbyggelinjer" },
            { source: "Miljø Portal", layer: "2013 Beskyttede naturtyper", title: "Beskyttede naturtyper" },
            { source: "Miljø Portal", layer: "2002 Beskyttede sten- og jorddiger", title: "Beskyttede sten- og jorddiger" },
            { source: "Matriklen2", layer: "StrandbeskyttelseLinje_Gaeldende", title: "Strandbeskyttelse" },
            { source: "Matriklen2", layer: "KlitfredningLinje_Gaeldende", title: "Klitfredning" },
            { source: "Miljøgis", layer: "Fredskov", title: "Fredskov" },
            { source: "Miljøgis", layer: "Naturmæssig særlig værdifuld skov", title: "Naturmæssig særlig værdifuld skov" },
            { source: "Miljøgis", layer: "Naturmæssigt særlig værdifuld skov på offentlige arealer (§25-skov)", title: "Naturmæssigt særlig værdifuld skov på offentlige arealer (§25-skov)" },
            { source: "FVM (Føde, Land og Fisk)", layer: "Jordbundskort_2024", title: "Jordbundskort" },
            { source: "Miljø Portal", layer: "2055 Råstofområder", title: "Råstofområder" },
            { source: "DKJord", layer: "Jordforurening, V1 kortlagt", title: "Jordforurening, V1 kortlagt" },
            { source: "DKJord", layer: "Jordforurening, V2 kortlagt", title: "Jordforurening, V2 kortlagt" },
            { source: "DKJord", layer: "Jordforurening, nuanceret", title: "Jordforurening, nuanceret" },
            { source: "Miljø Portal", layer: "2097 Igangværende påbud JFL", title: "Igangværende påbud JFL" },
            { source: "Plandata.dk", layer: "theme_pdk_planlagttrafikanlaeg_vedtaget", title: "Planlagt trafikanlæg vedtaget" },
            { source: "Plandata.dk", layer: "theme_pdk_planlagttrafikanlaeg_forslag", title: "Planlagt trafikanlæg forslag" },
            { source: "Plandata.dk", layer: "theme_pdk_vaerdifuldtlandbrugsomraade_vedtaget", title: "Værdifuldt landbrugsområde vedtaget" },
            { source: "Plandata.dk", layer: "theme_pdk_lokalplan_vedtaget", title: "Lokalplan vedtaget" },
            { source: "Plandata.dk", layer: "theme_pdk_lokalplan_forslag", title: "Lokalplan forslag" },
            { source: "Plandata.dk", layer: "theme_pdk_kommuneplanramme_vedtaget_v", title: "Kommuneplanramme vedtaget" },
            { source: "Plandata.dk", layer: "theme_pdk_kommuneplanramme_forslag_v", title: "Kommuneplanramme forslag" },
            { source: "Plandata.dk", layer: "theme_pdk_lokalplan_forslag_vindmoelle", title: "Lokalplan forslag Vindmølle" },
            { source: "Plandata.dk", layer: "theme_pdk_lokalplan_vedtaget_vindmoelle", title: "Lokalplan vedtaget Vindmølle" },
         ]
      }
   ];
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const accessibleProfiles = useMemo(() => profiles.filter(p => hasAccessToFeature(p.feature)), [hasAccessToFeature]);
   const [selectedProfileId, setSelectedProfileId] = useState<string | undefined>(() => accessibleProfiles[0]?.id);
   useEffect(() => {
      const isCurrentAccessible = !!accessibleProfiles.find(p => p.id === selectedProfileId);
      if (!isCurrentAccessible) {
         setSelectedProfileId(accessibleProfiles[0]?.id);
      }
   }, [accessibleProfiles, selectedProfileId]);

   const [searchMode, setSearchMode] = useState<'profile' | 'datasource'>('profile');

   // Clear error and resultMessage when selectedLayer, bufferDistance, or selectedProfileId changes
   useEffect(() => {
      setError(null);
      setResultMessage(null);
      setFoundFeatures(null);
      setPendingConflicts([]);
   }, [searchMode, selectedLayer, bufferDistance, selectedProfileId]);

   const mapitStateRef = useRef(mapitState);
   useEffect(() => { mapitStateRef.current = mapitState; }, [mapitState]);

   const searchModeRef = useRef(searchMode);
   useEffect(() => { searchModeRef.current = searchMode; }, [searchMode]);

   async function createLayersFromResults(resultsArr: any[]) {
      const toCreate = (resultsArr || []).filter((c: any) => (c.matchedCount || 0) > 0);
      if (toCreate.length === 0) {
         setResultMessage(Localization.getText("No matching features to create layers from"));
         setPendingConflicts([]);
         setFoundFeatures(null);
         return;
      }

      const createPromises: Promise<LayerInfo>[] = toCreate.map((c: any) => new Promise<LayerInfo>((resolve, reject) => {
         const datasetNameToUse = c.layerName || c.displayName || "geojson";
         Persistence.createLayersFromGeoJSON(datasetNameToUse, c.result, new Date(), (li: LayerInfo) => {
            try {
               // Dispatch AddDataLayer with a callback so the reducer returns the authoritative LayerInfo
               // (with assigned layerId). Resolve the promise when onLayerAdded is invoked.
               mapitStateDispatch({
                  type: MapitStateActionType.AddDataLayer,
                  payload: { layerInfo: li, zoomToFocus: false },
                  callbacks: {
                     onLayerAdded: (createdLi: LayerInfo) => {
                        try {
                           resolve(createdLi);
                        } catch (err) {
                           reject(err);
                        }
                     }
                  }
               } as any);
            } catch (err) {
               reject(err);
            }
         });
      }));

      const createdLayerInfos = await Promise.all(createPromises);
      const createdLayerIds = createdLayerInfos.map(li => li.layerId);

      if (createdLayerIds.length > 1) {
         try {
            const latestState = mapitStateRef.current || mapitState;
            const baseHierarchy = latestState?.layerHierarchy || { id: 0, name: "Top Level", isCollapsed: false, children: [] };
            const newHierarchy: LayerHierarchy = JSON.parse(JSON.stringify(baseHierarchy));
            const newGroupId = OldHierarchy.extractLowestId(baseHierarchy) - 1;
            const groupNameToUse = (resultsArr && resultsArr[0] && resultsArr[0].groupName) ? resultsArr[0].groupName : Localization.getText("Group");
            const newGroup: LayerHierarchy = { id: newGroupId, name: groupNameToUse, isCollapsed: false, children: [] };
            newHierarchy.children = [newGroup, ...(newHierarchy.children || [])];
            createdLayerIds.forEach((layerId) => {
               OldHierarchy.removeLayerInHierarchy(layerId, newHierarchy);
               newGroup.children.push({ id: layerId } as LayerHierarchyEndNode);
            });
            mapitStateDispatch({ type: MapitStateActionType.SetLayerHierarchy, payload: { hierarchy: newHierarchy } });
         } catch (e) {
            console.error("Failed to create group for new layers", e);
         }
      }
      setPendingConflicts([]);
      setFoundFeatures(null);
   }

   useEffect(() => {
      setFeatureType(Object.keys(wfsEndpoints[endpoint]?.featureTypes ?? {})[0] as keyof typeof wfsEndpoints[typeof endpoint]["featureTypes"]);
   }, [endpoint]);

   const selectableLayers = useMemo(() => {
      let options = Object.values(mapitState.layers).filter(layer => layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer);
      setSelectedLayerId((prev) => prev ? prev : options.length > 0 ? options[0].layerId : undefined);
      return options;
   }, [mapitState.layers])

   useEffect(() => {
      if (mapitState.map) {
         let map = mapitState.map.getMapPop();
         addFill(map, "conflict-search-area", "#d6c251");
         addOutline(map, "found-features", "#0000FF");
         addPoint(map, "found-features", "#0000FF");
         return () => {
            removeLayer(map, "conflict-search-area");
            removeLayer(map, "found-features");
         }
      }
   }, [mapitState.map]);

   useEffect(() => {
      if (mapitState.map && selectedLayer) {
         let geom = mapitState.layers[selectedLayer]?.geoJson;
         let map = mapitState.map.getMapPop();
         setGeom(map, "conflict-search-area", buffer(geom, bufferDistance || 0, { units: 'meters' }));
      }
   }, [mapitState.map, selectedLayer, bufferDistance]);

   useEffect(() => {
      if (mapitState.map && foundFeatures) {
         let geom = foundFeatures;
         let map = mapitState.map.getMapPop();
         setGeom(map, "found-features", geom);
      }
   }, [mapitState.map, foundFeatures]);

   async function searchSingleLayerForConflicts(selectedLayer: number | undefined, endpoint: keyof typeof wfsEndpoints, featureType: keyof typeof wfsEndpoints[typeof endpoint]["featureTypes"]): Promise<any> {
      if (!selectedLayer) throw new Error("No selected layer");
      let x = mapitState.layers[selectedLayer]?.geoJson;
      if (bufferDistance) {
         x = buffer(x, bufferDistance, { units: 'meters' });
      }
      if (!x) throw new Error("No geometry for selected layer");
      return await DataCollector.WFS.getFeatureInsidePolygon(endpoint, featureType!, x as any, {
         whoami: wfsEndpoints[endpoint]?.["whoAmI"] ? (whoAmI ?? undefined) : undefined
      })
   }

   function handleClickProfileSearch() {
      const profile = accessibleProfiles.find(p => p.id === selectedProfileId);
      if (!profile) {
         setError(Localization.getText("No profile selected"));
         return;
      }
      // Start a fresh search: clear the output box and show the spinner for the overall run.
      setPendingConflicts([]);
      setFoundFeatures(null);
      setIsDownloading(true);
      setError(null);
      setResultMessage(null);

      let locale = Localization.getLocale()
      const ts = new Date().toLocaleDateString(locale) + "_" + new Date().toLocaleTimeString(locale);
      async function profileSearch() {
         let collectedResults: PendingConflict[] = [];
         let current = 0;
         for (const layer of profile?.layers || []) {
            try {
               let searchResult = await searchSingleLayerForConflicts(selectedLayer, layer.source as keyof typeof wfsEndpoints, layer.layer as keyof typeof wfsEndpoints[typeof endpoint]["featureTypes"]);
               collectedResults.push({
                  groupName: `${profile?.name} ${ts}`,
                  layerName: layer.layer,
                  title: layer.title,
                  source: layer.source,
                  result: searchResult,
                  matchedCount: searchResult?.metadata?.numberMatched || (searchResult?.features && searchResult.features.length) || 0
               });
            } catch (e) {
               collectedResults.push({
                  groupName: `${profile?.name} ${ts}`,
                  layerName: layer.layer,
                  title: layer.title,
                  source: layer.source,
                  result: null,
                  err: (e as Error)?.message || "Unknown error",
                  matchedCount: 0
               });
            }
            message.progress(Localization.getText("Profile conflict search progress..."), current / (profile?.layers.length || 1));
         }
         setPendingConflicts(collectedResults);
         windowDispatch(openWindow(WindowId.ConflictSearchResults, {
            results: collectedResults,
            originMode: 'profile',
            onSelect: (item: any) => {
               // Only set dialog preview when the dialog is still in the originating mode§
               if (searchModeRef.current === 'profile') {
                  setFoundFeatures(item?.result || item);
               }
            },
            onAdd: async (resultsArr: any[]) => {
               try {
                  await createLayersFromResults(resultsArr || collectedResults);
               } catch (e) {
                  setError((e as Error).message);
               }
            }
         }));
         let totalErrors = collectedResults.reduce((sum, c) => sum + (c.err ? 1 : 0), 0);
         if (totalErrors > 0) {
            setError(Localization.getFormattedText("Profile {profile}: {count} layers returned with errors", { profile: profile?.name, count: totalErrors }));
            setResultMessage(null);
            setPendingConflicts([]);
         } else {
            const totalMatches = collectedResults.reduce((sum, c) => sum + (c.matchedCount || 0), 0);
            if (totalMatches === 0) {
               setResultMessage(Localization.getText("No conflicts found for profile") + ` ${profile?.name}`);
            }
         }
         message.progress();
         setIsDownloading(false);

      }
      profileSearch();
   }

   function openResultsWindow(resultsArr?: any[]) {
      const resultsToShow = resultsArr ?? pendingConflicts;
      if (!resultsToShow || resultsToShow.length === 0) return;
      // determine origin: if showing the current pendingConflicts reference, treat as 'profile'
      const originMode: 'profile' | 'datasource' = resultsToShow === pendingConflicts ? 'profile' : 'datasource';
      windowDispatch(openWindow(WindowId.ConflictSearchResults, {
         results: resultsToShow,
         originMode,
         onSelect: (item: any) => {
            if (searchModeRef.current === originMode) {
               setFoundFeatures(item?.result || item);
            }
         },
         onAdd: async (resultsArrInner: any[]) => {
            try {
               await createLayersFromResults(resultsArrInner || resultsToShow);
            } catch (e) {
               setError((e as Error).message);
            }
         }
      }));
   }

   return (
      <AdvancedDragModal
         PosDefault={{ left: "60px", top: "60px", width: "360px", height: "600px" }}
         title={Localization.getText("Conflict search")}
         onClose={() => windowDispatch(closeWindow(window.id))}
         subHeaderContent={<ViewButtonText disabled style={{ background: "orange", pointerEvents: "none", marginRight: "auto" }} > Early preview ⁉️</ViewButtonText>}
      >
         <ADModalBody>
            <GlassInputGroup >
               <GlassInputGroupTitle>{Localization.getText("Search area")}</GlassInputGroupTitle>
               <GlassInputControlLabel>{""}</GlassInputControlLabel>
               <SelectLayer selectedLayerId={selectedLayer || ""} onSelect={(e) => { setSelectedLayerId(e); }} availableLayers={selectableLayers} />
               <GlassInputControlLabel>{Localization.getText("Buffer")}</GlassInputControlLabel>
               <GlassInputControl><input type="number" value={bufferDistance} onChange={(e) => setBufferDistance(Number(e.target.value))} /><span>m</span></GlassInputControl>

               {/* Mode selector: choose to work with a Profile or a Datasource */}
               <GlassInputGroupTitle>{Localization.getText("Mode")}</GlassInputGroupTitle>
               <GlassInputControlLabel>{""}</GlassInputControlLabel>
               <GlassInputControl>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                     <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="radio" name="conflict-search-mode" value="profile" checked={searchMode === 'profile'} onChange={() => setSearchMode('profile')} />
                        <span>{Localization.getText("Group of layers")}</span>
                     </label>
                     <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="radio" name="conflict-search-mode" value="datasource" checked={searchMode === 'datasource'} onChange={() => setSearchMode('datasource')} />
                        <span>{Localization.getText("Single layer")}</span>
                     </label>
                  </div>
               </GlassInputControl>

               {/* Profile controls (only visible when searchMode === 'profile') */}
               {searchMode === 'profile' && (
                  <>
                     <GlassInputGroupTitle>{Localization.getText("Group of layers")}</GlassInputGroupTitle>
                     <GlassInputControlLabel>{""}</GlassInputControlLabel>
                     <GlassInputControl>
                        <select value={selectedProfileId} onChange={(e) => setSelectedProfileId(e.target.value)}>
                           {profiles.map((p) => (
                              <ProtectedFeature key={p.id} feature={p.feature} contentsIfNoAccess={null}>
                                 <option value={p.id}>{p.name}</option>
                              </ProtectedFeature>
                           ))}
                        </select>
                     </GlassInputControl>

                     <GlassInputControlLabel>{Localization.getText("Action")}</GlassInputControlLabel>
                     <div style={{ paddingTop: 4, display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                        <GlassButton style={{ width: '100%' }} disabled={!selectedProfileId || !featureType || !endpoint} onClick={() => handleClickProfileSearch()}>
                           {Localization.getText("Search for conflicts in profile")}
                        </GlassButton>
                     </div>
                  </>
               )}

               {/* Datasource controls (only visible when searchMode === 'datasource') */}
               {searchMode === 'datasource' && (
                  <>
                     <GlassInputGroupTitle>{Localization.getText("Single layer")}</GlassInputGroupTitle>
                     <GlassInputControlLabel>{Localization.getText("Register")}</GlassInputControlLabel>
                     <GlassInputControl>
                        <select value={endpoint} onChange={(e) => setEndpoint(e.target.value as keyof typeof wfsEndpoints)}>
                           {Object.keys(wfsEndpoints).filter((a) => wfsEndpoints[a].active !== false).map((key) => (
                              <option key={key} value={key}>{key}</option>
                           ))}
                        </select>
                     </GlassInputControl>
                     {wfsEndpoints[endpoint]?.["whoAmI"] ?
                        <>
                           <GlassInputControlLabel>{Localization.getText("Email")}</GlassInputControlLabel>
                           <GlassInputControl>
                              <input type="email" value={whoAmI ?? ""} onChange={(e) => setWhoAmI(e.target.value)} />
                           </GlassInputControl>
                        </>
                        : <></>
                     }

                     <GlassInputControlLabel>{Localization.getText("Layer")}</GlassInputControlLabel>
                     <GlassInputControl>
                        <select value={featureType} onChange={(e) => setFeatureType(e.target.value as keyof typeof wfsEndpoints[typeof endpoint]["featureTypes"])}>
                           {Object.keys(wfsEndpoints[endpoint]?.featureTypes ?? {}).map((key) => (
                              <option key={key} value={key}>{key}</option>
                           ))}
                        </select>
                     </GlassInputControl>
                     <ProtectedFeature feature={Feature.Debugging} contentsIfNoAccess={<></>} >
                        {SettingsManager.getSystemSetting("debuggingShowExtraDebugging") ?
                           <>
                              <GlassInputControlLabel>Debug</GlassInputControlLabel>
                              <GlassButtonRow wrap={true}>
                                 <GlassButton disabled={!endpoint} onClick={async () => (console.log(await DataCollector.WFS.getCapabilities(endpoint)))} >
                                    GetCapabilities
                                 </GlassButton>
                                 <GlassButton disabled={!featureType} onClick={async () => {
                                    await DataCollector.WFS.describeFeatureType(endpoint, featureType!);
                                 }} >
                                    DescribeFeatureType
                                 </GlassButton>
                                 <GlassButton disabled={!selectedLayer || !featureType} onClick={async () => {
                                    if (selectedLayer) {
                                       let x = buffer(mapitState.layers[selectedLayer]?.geoJson, bufferDistance || 0, { units: 'meters' });
                                       if (x) {
                                          setFoundFeatures(
                                             await DataCollector.WFS.getFeatureBBOX(endpoint, featureType!, bbox(x).flat() as [number, number, number, number])
                                          );
                                       }
                                    }
                                 }} >
                                    GetFeature BBOX
                                 </GlassButton>

                              </GlassButtonRow>
                           </> : <></>}
                     </ProtectedFeature>
                     <GlassInputControlLabel>{Localization.getText("Action")}</GlassInputControlLabel>
                     {/* Stack action buttons vertically and make them full width so Run appears directly under Fetch */}
                     <div style={{ paddingTop: 4, display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                        <GlassButton style={{ width: '100%' }} disabled={!selectedLayer || !featureType} onClick={async () => {
                           if (selectedLayer) {
                              let x = mapitState.layers[selectedLayer]?.geoJson;
                              if (bufferDistance) {
                                 x = buffer(x, bufferDistance, { units: 'meters' });
                              }
                              if (x) {
                                 try {
                                    setIsDownloading(true);
                                    setError(null);
                                    setFoundFeatures(null);
                                    DataCollector.WFS.getFeatureInsidePolygon(endpoint, featureType!, x as any, {
                                       whoami: wfsEndpoints[endpoint]?.["whoAmI"] ? (whoAmI ?? undefined) : undefined
                                    }).then((result) => {
                                       setIsDownloading(false);
                                       setFoundFeatures(result);
                                    }).catch((err) => {
                                       setIsDownloading(false);
                                       console.error(err);
                                       setError(err.message);
                                    })
                                 } catch (e) {
                                    setError((e as Error).message);
                                    return;
                                 }
                              }
                           }
                        }} >
                           {Localization.getText("Fetch")}
                        </GlassButton>
                     </div>
                  </>
               )}

               {/* Single conditional output area: only render when we actually have something to show */}
               {(isDownloading || error || resultMessage || (pendingConflicts && pendingConflicts.length > 0) || foundFeatures) && (
                  <>
                     {/* Show 'Info' label to the left when we have feature info to display */}
                     <GlassInputControlLabel>{foundFeatures ? Localization.getText("Info") : ""}</GlassInputControlLabel>
                     <GlassInputControl>
                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', textAlign: 'center', width: '100%' }}>
                           {isDownloading && (
                              <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                                 {/* Use standard small project spinner and position it at top of the output box */}
                                 <Spinner size="sm" />
                              </div>
                           )}

                           {error && (
                              <div>
                                 <div style={{ fontWeight: 600, color: "red" }}>{Localization.getText("Error")}</div>
                                 <div style={{ color: "red" }}>{error}</div>
                              </div>
                           )}

                           {resultMessage && (
                              <div style={{ color: "#0b6623", fontWeight: 500 }}>{resultMessage}</div>
                           )}

                           {pendingConflicts && pendingConflicts.length > 0 && (
                              <div style={{ width: '100%', maxWidth: 320, textAlign: 'center' }}>
                                 <div style={{ fontWeight: 600 }}>{Localization.getText("Found conflicts")}</div>
                                 <div>{Localization.getText("Results are shown in a separate window")}</div>
                                 {/* Hide the 'Open results window' button when the results window is already open */}
                                 {!isWindowOpen(windowState, WindowId.ConflictSearchResults) && (
                                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
                                       <GlassButton onClick={() => openResultsWindow()}>{Localization.getText("Open results window")}</GlassButton>
                                    </div>
                                 )}
                              </div>
                           )}

                           {foundFeatures && (
                              <div style={{ width: '100%', maxWidth: 320, textAlign: 'left' }}>
                                 <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                                    <tbody>
                                       <tr>
                                          <td style={{ width: '40%', padding: '4px 8px', fontWeight: 600 }}>{Localization.getText("Single layer")}</td>
                                          <td style={{ padding: '4px 8px', wordBreak: 'break-word', whiteSpace: 'normal' }}>{foundFeatures?.metadata?.endpoint}</td>
                                       </tr>
                                       <tr>
                                          <td style={{ width: '40%', padding: '4px 8px', fontWeight: 600 }}>{Localization.getText("Matched")}</td>
                                          <td style={{ padding: '4px 8px' }}>{foundFeatures?.metadata?.numberMatched}</td>
                                       </tr>
                                       <tr>
                                          <td style={{ width: '40%', padding: '4px 8px', fontWeight: 600 }}>{Localization.getText("Returned")}</td>
                                          <td style={{ padding: '4px 8px' }}>{foundFeatures?.metadata?.numberReturned}</td>
                                       </tr>
                                    </tbody>
                                 </table>
                              </div>
                           )}
                        </div>
                     </GlassInputControl>
                  </>
               )}
            </GlassInputGroup>

         </ADModalBody>
         <ADModalFooter >
            <GlassButton disabled={!(foundFeatures || (pendingConflicts && pendingConflicts.length > 0))} onClick={async () => {
               // If there are pending conflicts from a profile run, add those. Otherwise fall back to single fetched foundFeatures.
               try {
                  if (pendingConflicts && pendingConflicts.length > 0) {
                     await createLayersFromResults(pendingConflicts);
                  } else if (foundFeatures) {
                     Persistence.createLayersFromGeoJSON(featureType || "From Conflict", foundFeatures, new Date(), (li: LayerInfo) => { mapitStateDispatch({ type: MapitStateActionType.AddDataLayer, payload: { layerInfo: li, zoomToFocus: false } }) });
                     setResultMessage(Localization.getText("Created 1 layer from single fetch result"));
                     setFoundFeatures(null);
                  }
               } catch (e) {
                  setError((e as Error).message);
               }
            }} >
               {Localization.getText("Add as layer")}
            </GlassButton>
            <GlassButton onClick={async () => {
               windowDispatch(closeWindow(window.id))
            }}>
               {Localization.getText("Close")}
            </GlassButton>

         </ADModalFooter>
      </AdvancedDragModal >
   );
}
